import { createSlice, PayloadAction } from "@reduxjs/toolkit"
import { InterviewDetails, Problem, TabKey } from "types/enterprise/assessment"
import { ASSESSMENT_TABS } from "utils/enterprise/assessment"

export interface AssessmentState {
  interviewTemplates: {
    pageNumber: number
    totalCount: number
    data: InterviewDetails[]
  }
  teamInterviews: {
    pageNumber: number
    totalCount: number
    data: InterviewDetails[]
  }
  activeTab: TabKey
  selectedInterview: InterviewDetails | null
  selectedProblems: Problem[]
  candidates: {
    pageNumber: number
    pageSize: number
    totalCount: number
    filters: {
      emails: string[]
    }
  }
}

const initialState: AssessmentState = {
  interviewTemplates: {
    data: [],
    pageNumber: 1,
    totalCount: 0,
  },
  teamInterviews: {
    data: [],
    pageNumber: 1,
    totalCount: 0,
  },
  activeTab: ASSESSMENT_TABS.CONFIGURATION,
  selectedInterview: null,
  selectedProblems: [],
  candidates: {
    pageNumber: 1,
    pageSize: 10,
    totalCount: 0,
    filters: {
      emails: [],
    },
  },
}

const assessmentSlice = createSlice({
  name: "assessment",
  initialState,
  reducers: {
    setInterviewTemplates: (state, action: PayloadAction<Partial<AssessmentState["interviewTemplates"]>>) => ({
      ...state,
      interviewTemplates: {
        ...state.interviewTemplates,
        ...action.payload,
      },
    }),
    setTeamInterviews: (state, action: PayloadAction<Partial<AssessmentState["teamInterviews"]>>) => ({
      ...state,
      teamInterviews: {
        ...state.teamInterviews,
        ...action.payload,
      },
    }),
    setSelectedInterview: (state, action: PayloadAction<InterviewDetails>) => ({
      ...state,
      selectedInterview: action.payload,
    }),
    setActiveTab: (state, action: PayloadAction<TabKey>) => ({
      ...state,
      activeTab: action.payload,
    }),
    addSelectedProblems: (state, action: PayloadAction<Problem[]>) => ({
      ...state,
      selectedProblems: action.payload,
    }),
    addSelectedProblem: (state, action: PayloadAction<Problem>) => ({
      ...state,
      selectedProblems: [...state.selectedProblems, action.payload],
    }),
    removeSelectedProblem: (state, action: PayloadAction<string>) => ({
      ...state,
      selectedProblems: state.selectedProblems.filter((problem) => problem.problem_id !== action.payload),
    }),
    setCandidatesPageNumber: (state, action: PayloadAction<number>) => ({
      ...state,
      candidates: {
        ...state.candidates,
        pageNumber: action.payload,
      },
    }),
    setCandidatesFilters: (state, action: PayloadAction<AssessmentState["candidates"]["filters"]>) => ({
      ...state,
      candidates: {
        ...state.candidates,
        filters: action.payload,
      },
    }),
    setCandidatesTotalCount: (state, action: PayloadAction<number>) => ({
      ...state,
      candidates: {
        ...state.candidates,
        totalCount: action.payload,
      },
    }),
    setCandidatesPageSize: (state, action: PayloadAction<number>) => ({
      ...state,
      candidates: {
        ...state.candidates,
        pageSize: action.payload,
      },
    }),
  },
})

export const {
  setInterviewTemplates,
  setTeamInterviews,
  setSelectedInterview,
  setActiveTab,
  addSelectedProblems,
  addSelectedProblem,
  removeSelectedProblem,
  setCandidatesPageNumber,
  setCandidatesFilters,
  setCandidatesTotalCount,
  setCandidatesPageSize,
} = assessmentSlice.actions

export default assessmentSlice.reducer
